import { useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Map as MapIcon, List, Search, MapPin, Locate, X, ChevronLeft, ChevronRight, Thermometer, Droplets, Zap, Flame, Snowflake, Wind } from 'lucide-react';
import { MapView } from '@/components/map/MapView';
import { ProviderCard } from '@/components/listings/ProviderCard';
import type { ServiceProvider, SearchFilters, MapBounds, ServiceType } from '@/types';
import { useProviders } from '@/hooks/useProviders';
import { SERVICE_TYPE_LABELS } from '@/types';

// Service type configuration for filters
const serviceTypeConfig: {
  type: ServiceType;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  bgColor: string;
  activeColor: string;
}[] = [
  { type: 'hvac', icon: Thermometer, color: 'text-blue-600', bgColor: 'bg-blue-100', activeColor: 'bg-blue-600 text-white' },
  { type: 'plumbing', icon: Droplets, color: 'text-cyan-600', bgColor: 'bg-cyan-100', activeColor: 'bg-cyan-600 text-white' },
  { type: 'electrical', icon: Zap, color: 'text-yellow-600', bgColor: 'bg-yellow-100', activeColor: 'bg-yellow-600 text-white' },
  { type: 'heating', icon: Flame, color: 'text-orange-600', bgColor: 'bg-orange-100', activeColor: 'bg-orange-600 text-white' },
  { type: 'cooling', icon: Snowflake, color: 'text-sky-600', bgColor: 'bg-sky-100', activeColor: 'bg-sky-600 text-white' },
  { type: 'ventilation', icon: Wind, color: 'text-teal-600', bgColor: 'bg-teal-100', activeColor: 'bg-teal-600 text-white' },
];

interface MapSearchSectionProps {
  /** Initial filters to apply */
  initialFilters?: SearchFilters;
  /** Maximum number of providers to show in list (for compact mode) */
  maxProviders?: number;
  /** Height of the section */
  height?: string;
  /** Show "View All" link */
  showViewAll?: boolean;
  /** Custom title for the section */
  title?: string;
  /** Custom subtitle for the section */
  subtitle?: string;
  /** Variant: 'full' shows list + map side by side, 'embedded' shows map with overlay cards */
  variant?: 'full' | 'embedded';
  /** Class name for the container */
  className?: string;
  /** Show category filters on the map */
  showCategoryFilters?: boolean;
}

export function MapSearchSection({
  initialFilters = {},
  maxProviders = 6,
  height = '600px',
  showViewAll = true,
  title,
  subtitle,
  variant = 'embedded',
  className = '',
  showCategoryFilters = false,
}: MapSearchSectionProps) {
  const { t } = useTranslation(['home', 'search']);
  const [hoveredProviderId, setHoveredProviderId] = useState<string | null>(null);
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(null);
  const [mapBounds, setMapBounds] = useState<MapBounds | undefined>();
  const [showMap, setShowMap] = useState(true);
  const [locationQuery, setLocationQuery] = useState('');
  const [isLocating, setIsLocating] = useState(false);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [mobileCardIndex, setMobileCardIndex] = useState(0);
  const [selectedServiceType, setSelectedServiceType] = useState<ServiceType | null>(null);
  const mobileScrollRef = useRef<HTMLDivElement>(null);

  // Merge location query and service type into filters
  const filters: SearchFilters = {
    ...initialFilters,
    ...(locationQuery ? { location: locationQuery } : {}),
    ...(selectedServiceType ? { serviceTypes: [selectedServiceType] } : {}),
  };

  const { data: providers = [], isLoading } = useProviders(filters, mapBounds);

  const handleBoundsChange = useCallback((bounds: MapBounds) => {
    setMapBounds(bounds);
  }, []);

  const handleProviderSelect = useCallback((provider: ServiceProvider | null) => {
    setSelectedProviderId(provider?.id || null);
  }, []);

  const handleUseMyLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError(t('search:locationNotSupported', 'Location not supported'));
      return;
    }

    setIsLocating(true);
    setLocationError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
        setLocationQuery('');
        setIsLocating(false);
      },
      (error) => {
        setIsLocating(false);
        if (error.code === error.PERMISSION_DENIED) {
          setLocationError(t('search:locationDenied', 'Location access denied'));
        } else {
          setLocationError(t('search:locationError', 'Could not get location'));
        }
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [t]);

  const clearLocation = useCallback(() => {
    setLocationQuery('');
    setUserLocation(null);
    setLocationError(null);
  }, []);

  const displayedProviders = providers.slice(0, maxProviders);
  const hasMore = providers.length > maxProviders;

  const scrollMobileCards = useCallback((direction: 'left' | 'right') => {
    const container = mobileScrollRef.current;
    if (!container) return;
    const cardWidth = 300;
    const maxIndex = Math.max(0, displayedProviders.length - 1);
    const newIndex = direction === 'left'
      ? Math.max(0, mobileCardIndex - 1)
      : Math.min(maxIndex, mobileCardIndex + 1);
    container.scrollTo({ left: newIndex * cardWidth, behavior: 'smooth' });
    setMobileCardIndex(newIndex);
  }, [mobileCardIndex, displayedProviders.length]);

  if (variant === 'full') {
    return (
      <div className={`${className}`}>
        {(title || subtitle) && (
          <div className="text-center mb-8">
            {title && <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">{title}</h2>}
            {subtitle && <p className="text-lg text-gray-600 max-w-2xl mx-auto">{subtitle}</p>}
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-6" style={{ height }}>
          {/* Provider List */}
          <div className="w-full lg:w-2/5 overflow-y-auto space-y-4 pr-2">
            <div className="flex items-center justify-between mb-4 lg:hidden">
              <p className="text-sm text-gray-600">
                {isLoading ? t('search:loading') : `${providers.length} pros found`}
              </p>
              <button
                onClick={() => setShowMap(!showMap)}
                className="btn btn-secondary btn-sm flex items-center gap-2"
              >
                {showMap ? <List className="w-4 h-4" /> : <MapIcon className="w-4 h-4" />}
                {showMap ? 'List' : 'Map'}
              </button>
            </div>

            <div className={`space-y-4 ${showMap ? 'hidden lg:block' : ''}`}>
              {displayedProviders.map((provider) => (
                <ProviderCard
                  key={provider.id}
                  provider={provider}
                  onHover={setHoveredProviderId}
                  isHighlighted={selectedProviderId === provider.id}
                />
              ))}

              {!isLoading && providers.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  No providers found in this area
                </div>
              )}

              {showViewAll && hasMore && (
                <Link
                  to="/search"
                  className="block text-center py-4 text-primary-600 hover:text-primary-700 font-medium"
                >
                  View all {providers.length} pros →
                </Link>
              )}
            </div>
          </div>

          {/* Map */}
          <div className={`flex-1 rounded-2xl overflow-hidden shadow-lg ${!showMap ? 'hidden lg:block' : ''}`}>
            <MapView
              providers={providers}
              onBoundsChange={handleBoundsChange}
              selectedProviderId={selectedProviderId}
              onProviderSelect={handleProviderSelect}
              hoveredProviderId={hoveredProviderId}
              flyToSelected
              fitBounds
            />
          </div>
        </div>
      </div>
    );
  }

  // Embedded variant - map with floating cards
  return (
    <section className={`relative ${className}`}>
      {/* Header with title and location search */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="mb-8"
      >
        {(title || subtitle) && (
          <div className="text-center mb-6">
            {title && (
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-3">
                {title}
              </h2>
            )}
            {subtitle && (
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {subtitle}
              </p>
            )}
          </div>
        )}

        {/* Location Search Bar */}
        <div className="max-w-xl mx-auto">
          <div className="relative flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={locationQuery}
                onChange={(e) => setLocationQuery(e.target.value)}
                placeholder={t('search:enterLocation', 'Enter city, state, or zip code...')}
                className="w-full pl-12 pr-10 py-3.5 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent shadow-sm transition-all"
              />
              {(locationQuery || userLocation) && (
                <button
                  onClick={clearLocation}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <button
              onClick={handleUseMyLocation}
              disabled={isLocating}
              className={`
                flex items-center gap-2 px-4 py-3.5 rounded-xl font-medium text-sm transition-all
                ${userLocation
                  ? 'bg-primary-100 text-primary-700 border border-primary-200'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                }
                ${isLocating ? 'opacity-70 cursor-wait' : ''}
              `}
            >
              <Locate className={`w-5 h-5 ${isLocating ? 'animate-pulse' : ''}`} />
              <span className="hidden sm:inline">
                {isLocating ? t('search:locating', 'Locating...') : userLocation ? t('search:nearMe', 'Near me') : t('search:useLocation', 'Use location')}
              </span>
            </button>
          </div>

          {/* Location Error */}
          <AnimatePresence>
            {locationError && (
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="mt-2 text-sm text-red-600 text-center"
              >
                {locationError}
              </motion.p>
            )}
          </AnimatePresence>

          {/* Active Location Indicator */}
          <AnimatePresence>
            {userLocation && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="mt-3 flex items-center justify-center gap-2 text-sm text-primary-700"
              >
                <MapPin className="w-4 h-4" />
                <span>{t('search:showingNearYou', 'Showing pros near your location')}</span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>

      {/* Map Container */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.1 }}
        className="relative rounded-3xl overflow-hidden shadow-2xl bg-gray-100"
        style={{ height }}
      >
        {/* Decorative gradient overlay at top */}
        <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-gray-900/10 to-transparent z-[1000] pointer-events-none" />

        {/* Map */}
        <MapView
          providers={providers}
          onBoundsChange={handleBoundsChange}
          selectedProviderId={selectedProviderId}
          onProviderSelect={handleProviderSelect}
          hoveredProviderId={hoveredProviderId}
          flyToSelected={false}
          center={userLocation || undefined}
        />

        {/* Category Filters Overlay - Bottom right */}
        {showCategoryFilters && (
          <div className="absolute bottom-4 right-4 z-[1000]">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg border border-gray-100 p-2"
            >
              {selectedServiceType && (
                <button
                  onClick={() => setSelectedServiceType(null)}
                  className="mb-2 w-full text-xs text-gray-500 hover:text-gray-700 transition-colors flex items-center justify-center gap-1"
                >
                  <X className="w-3 h-3" />
                  {t('search:clearFilter', 'Clear filter')}
                </button>
              )}
              <div className="flex flex-col gap-1.5 lg:flex-row lg:flex-wrap lg:max-w-[400px]">
                {serviceTypeConfig.map((config) => {
                  const Icon = config.icon;
                  const isActive = selectedServiceType === config.type;
                  return (
                    <button
                      key={config.type}
                      onClick={() => setSelectedServiceType(isActive ? null : config.type)}
                      className={`
                        flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium
                        transition-all duration-200 hover:scale-105
                        ${isActive
                          ? config.activeColor
                          : `${config.bgColor} ${config.color} hover:opacity-80`
                        }
                      `}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{SERVICE_TYPE_LABELS[config.type]}</span>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </div>
        )}

        {/* Desktop: Floating Cards Overlay - Bottom left (avoiding zoom controls) */}
        <div className="absolute left-4 bottom-4 w-80 max-h-[320px] hidden lg:flex flex-col gap-3 overflow-y-auto pointer-events-none scrollbar-hide z-[1000]">
          <AnimatePresence mode="popLayout">
            {displayedProviders.slice(0, 4).map((provider, index) => (
              <motion.div
                key={provider.id}
                layout
                initial={{ opacity: 0, x: -30, scale: 0.95 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: -20, scale: 0.95 }}
                transition={{
                  delay: index * 0.08,
                  type: 'spring',
                  stiffness: 300,
                  damping: 25
                }}
                className="pointer-events-auto"
              >
                <ProviderCard
                  provider={provider}
                  onHover={setHoveredProviderId}
                  isHighlighted={selectedProviderId === provider.id}
                  compact
                />
              </motion.div>
            ))}
          </AnimatePresence>

          {/* Empty State */}
          {!isLoading && providers.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-white/95 backdrop-blur-sm rounded-xl p-6 text-center pointer-events-auto shadow-lg"
            >
              <MapPin className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600 font-medium">
                {t('search:noProviders', 'No providers found')}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {t('search:tryAdjusting', 'Try adjusting your search or location')}
              </p>
            </motion.div>
          )}
        </div>

        {/* Mobile: Bottom Card Carousel */}
        <div className="absolute left-0 right-0 bottom-0 lg:hidden z-[1000]">
          {/* Gradient backdrop */}
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-gray-900/40 to-transparent pointer-events-none" />

          <div className="relative p-4 pt-8">
            {/* Navigation Arrows */}
            {displayedProviders.length > 1 && (
              <>
                <button
                  onClick={() => scrollMobileCards('left')}
                  disabled={mobileCardIndex === 0}
                  className={`
                    absolute left-2 top-1/2 -translate-y-1/2 z-10
                    w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm shadow-lg
                    flex items-center justify-center transition-all
                    ${mobileCardIndex === 0 ? 'opacity-40 cursor-not-allowed' : 'hover:bg-white hover:scale-110'}
                  `}
                >
                  <ChevronLeft className="w-5 h-5 text-gray-700" />
                </button>
                <button
                  onClick={() => scrollMobileCards('right')}
                  disabled={mobileCardIndex >= displayedProviders.length - 1}
                  className={`
                    absolute right-2 top-1/2 -translate-y-1/2 z-10
                    w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm shadow-lg
                    flex items-center justify-center transition-all
                    ${mobileCardIndex >= displayedProviders.length - 1 ? 'opacity-40 cursor-not-allowed' : 'hover:bg-white hover:scale-110'}
                  `}
                >
                  <ChevronRight className="w-5 h-5 text-gray-700" />
                </button>
              </>
            )}

            {/* Cards */}
            <div
              ref={mobileScrollRef}
              className="flex gap-4 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-hide scroll-smooth px-8"
              onScroll={(e) => {
                const index = Math.round(e.currentTarget.scrollLeft / 300);
                setMobileCardIndex(index);
              }}
            >
              {displayedProviders.map((provider, index) => (
                <motion.div
                  key={provider.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + index * 0.05 }}
                  className="flex-shrink-0 w-[280px] snap-center"
                >
                  <ProviderCard
                    provider={provider}
                    onHover={setHoveredProviderId}
                    isHighlighted={selectedProviderId === provider.id}
                    compact
                  />
                </motion.div>
              ))}
            </div>

            {/* Pagination Dots */}
            {displayedProviders.length > 1 && (
              <div className="flex justify-center gap-1.5 mt-3">
                {displayedProviders.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      mobileScrollRef.current?.scrollTo({
                        left: index * 300,
                        behavior: 'smooth',
                      });
                      setMobileCardIndex(index);
                    }}
                    className={`
                      transition-all duration-300
                      ${index === mobileCardIndex
                        ? 'w-6 h-2 bg-white rounded-full'
                        : 'w-2 h-2 bg-white/50 rounded-full hover:bg-white/70'
                      }
                    `}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Top Right Controls */}
        <div className="absolute top-4 right-4 flex flex-col gap-2 z-[1000]">
          {/* View All Button */}
          {showViewAll && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
            >
              <Link
                to={`/search${locationQuery ? `?location=${encodeURIComponent(locationQuery)}` : ''}`}
                className="bg-white hover:bg-gray-50 text-gray-900 px-4 py-2.5 rounded-xl shadow-lg flex items-center gap-2 font-medium text-sm transition-all hover:scale-105 border border-gray-100"
              >
                {t('search:viewAllPros', 'View all pros')}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          )}

          {/* Results count badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4 }}
            className="bg-white/95 backdrop-blur-sm px-4 py-2 rounded-xl shadow-lg text-sm font-medium text-gray-700 text-center border border-gray-100"
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                {t('search:loading', 'Loading...')}
              </span>
            ) : (
              <span>
                <span className="text-primary-600 font-bold">{providers.length}</span>
                {' '}{t('search:prosNearby', 'pros nearby')}
              </span>
            )}
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}
